import { createRouter, createWebHistory } from 'vue-router'
import routes from './routes'
import { tools } from '@sword-health/ui-core'
import { CONFIG, VERSION_FILE } from '@/scripts/app-configs/constants'
import store from '@/store'
import { triggers } from '@/scripts/global-modals-commands'
import { trackNewrelicRouteChange } from '@/utils/newrelic'
import { segment } from '@/utils/segment'

const VERSION_CHECK_DEBOUNCE = 10 * 1000 // set a 10 second debounce between version checks

const router = createRouter({
  history: createWebHistory(),
  routes,
})

const checkVersion = async (stateName, stateMetadata = {}) => {
  // check version of client;
  if (!stateMetadata.checkAppVersion) {
    return
  }
  await store.dispatch('system/importPortalVersion', VERSION_FILE)
  const appUpdated = store.getters['system/appUpdated']

  if (!appUpdated) {
    window.location.reload()
    console.warn('updated portal version with refresh')
  }
}

const ACLCheckOk = (stateMetadata = {}) => {
  const featureDisabled = stateMetadata.isFeatureEnabled === false
  const userAuthenticated = store.getters['user/authentication/isAuthenticated']
  const userCanAccess = userAuthenticated || stateMetadata.unauthorizedAccess === true

  return !featureDisabled && userCanAccess
}

router.beforeEach(async (to, from, next) => {
  document.title = process.env.NODE_ENV === 'development' ? 'DEV - SWORD Health' : 'SWORD Health'

  const userAuthenticated = store.getters['user/authentication/isAuthenticated'] || (() => {
    store.commit('user/authentication/loadStoredAuthTokens')

    return store.getters['user/authentication/isAuthenticated']
  })()

  if (to.name === 'logout' && userAuthenticated) {
    await store.dispatch('user/authentication/logout')

    return next({ name: 'login' })
  }

  /*
    Prevents access to '/login' if user is authenticated, unless
   'redirected' confirms logout state was already handled.
  */
  if ((to.name === 'login') && !to.query.redirected && userAuthenticated) {
    return next({ name: 'home' })
  }

  // Protecting against external malformed double slashed urls
  const externalSheetsProtectionRegex = new RegExp(/^(\/{2}(([a-zA-Z]).*))/)

  if (externalSheetsProtectionRegex.test(to.path)) {
    const safeRoutePath = to.path.replace(externalSheetsProtectionRegex, (a, b, c) => `/${c}`)

    return next({ path: safeRoutePath, replace: true })
  }

  // Protecting against old external prescriptions urls
  const externalSheetsProtectionPrescriptionRegex = new RegExp(/^(\/prescription\/([0-9]+)\/(?!session\/|recommendations))/)

  if (externalSheetsProtectionPrescriptionRegex.test(to.path)) {
    const patientID = to.path.match(externalSheetsProtectionPrescriptionRegex)[2]
    const { sessionID } = to.query

    if (!sessionID) {
      return next({ name: 'profile.sessions', params: { patientID }, replace: true })
    }

    return next({
      name: 'prescription.exercises',
      params: { patientID, sessionID },
      replace: true,
    })
  }

  tools.debounce(checkVersion.bind(this, to.name, to.meta), VERSION_CHECK_DEBOUNCE, true)()
  tools.debounce(() => store.dispatch('user/authentication/checkTokenValidity'), VERSION_CHECK_DEBOUNCE, true)()

  if (to.meta?.unauthorizedAccess === true) {
    store.commit('system/setUIReadyState', true)
  }

  if (!ACLCheckOk(to.meta)) {
    const shouldRedirectAfterLogin = !userAuthenticated && to.matched.length && to.matched.some((route) => route.path !== '*')

    return next({
      name: 'login',
      params: { redirectPath: shouldRedirectAfterLogin ? to.fullPath : null },
    })
  }

  return next()
})

let hasInternalNavigation
let initialRouteFullPath

router.isReady().then(() => {
  initialRouteFullPath = router.currentRoute.value.fullPath
})

router.afterEach((to, from, failure) => {
  if (!failure && CONFIG.enableAnalytics) {
    gtag('config', CONFIG.analyticsKey, { page_path: router.currentRoute.value.fullPath })
  }

  trackNewrelicRouteChange(from.fullPath, to.fullPath)

  if (!to.meta?.ignoreSegmentPageView) {
    segment.trackPageView(to, from)
  }

  try {
    triggers.closeChatPopup()
  } catch (error) {
    console.error(error)
  }

  if (from.matched.length !== 0 && to.fullPath !== from.fullPath) {
    hasInternalNavigation = true
  }
})

// A safe router.back() implementation that falls back to a default route to avoid exiting the app.
router.back = function backWithFallback(fallbackRoute = { name: 'feed-home' }, replace = false) {
  const isInitialPage = router.currentRoute.value.fullPath === initialRouteFullPath

  if (!hasInternalNavigation || isInitialPage) {
    return replace ? this.replace(fallbackRoute) : this.push(fallbackRoute)
  }

  return this.go(-1)
}

export default router
